import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon,
  CopyIcon,
} from "lucide-react";

import {
  DetailItem,
  DetailLabel,
  DetailValue,
  FlyoutContainer,
  HeaderContent,
} from "../../../components/Flyouts";

import AcquisitionsAPI from "@/api/acquisitions";
import { usePermissions } from "@/hooks/usePermissions";

import Acquisition from "@/types/Acquisitions/Acquisition";
import {
  EditAcquisitionModal,
  DeleteAcquisitionModal,
  CopyAcquisitionModal,
} from "../Modals";
import { Case } from "@/pages/Cases/types/Cases";
import { Evidence } from "../../Evidence";
import { useAcquisitionColumnDefinitions } from "../Hooks";

interface AcquisitionFlyoutProps {
  show: boolean;
  onClose?: () => void;
  defaultInfo: Acquisition;
  onEdit: () => void;
  onDelete: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onCopy?: () => void;
  caseInfo: Case;
  evidenceInfo?: Evidence;
}

interface FlyoutDetailsProps {
  defaultData: Acquisition;
}

interface FlyoutNavControlsProps {
  onPrevious?: () => void;
  onNext?: () => void;
}

const FlyoutNavControls: FC<FlyoutNavControlsProps> = ({
  onPrevious,
  onNext,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      gap: 10,
      alignItems: "center",
      marginLeft: "auto",
    }}
  >
    <Button
      size="xxs"
      variant="outlined"
      onClick={() => onPrevious?.()}
      style={{ padding: "2px 4px" }}
    >
      <ChevronLeftIcon size={14} />
    </Button>
    <Button
      size="xxs"
      variant="outlined"
      onClick={() => onNext?.()}
      style={{ padding: "2px 4px" }}
    >
      <ChevronRightIcon size={14} />
    </Button>
  </div>
);

const MainDetails: FC<FlyoutDetailsProps> = ({ defaultData }) => {
  // Render from the same column definitions the Stored Data table uses
  // (including custom-field columns) so captions and cell formatting can't
  // drift between the table and its flyout.
  const { columnDefinitions } = useAcquisitionColumnDefinitions();
  const dataFieldIgnoreList = ["acq_notes"];
  const CUSTOM_FIELD_PREFIX = "custom_field_";

  const data: Acquisition & Record<string, any> = {
    ...defaultData,
    ...(defaultData?.custom_attributes?.reduce(
      (acc: Record<string, any>, attr: any) => {
        acc[`${CUSTOM_FIELD_PREFIX}${attr.field_id}`] = attr.value;
        return acc;
      },
      {}
    ) || {}),
  };

  return (
    <FlyoutContainer>
      <div className="event-details">
        {columnDefinitions
          .filter((col) => !dataFieldIgnoreList.includes(col.dataField))
          .filter((col) =>
            col.dataField.startsWith(CUSTOM_FIELD_PREFIX)
              ? // Presence, not truthiness — a stored 0 or false is a real
                // value and must render like it does in the table.
                data[col.dataField] !== undefined &&
                data[col.dataField] !== null &&
                data[col.dataField] !== ""
              : true
          )
          .map((col) => {
            const key = col.dataField;
            const label = col.caption;
            let value: React.ReactNode;

            if (key === "status") {
              // The shared definition renders an interactive StatusSelector;
              // this details view is read-only, so show the plain value.
              value = data.status;
            } else if (col.render) {
              value = col.render({ rowData: data });
            } else {
              value = data[key];
            }

            return (
              <DetailItem key={key}>
                <DetailLabel>{label}</DetailLabel>
                <DetailValue>{value}</DetailValue>
              </DetailItem>
            );
          })}

        <DetailItem>
          <DetailLabel>Description</DetailLabel>
        </DetailItem>
        <DetailValue>
          {defaultData.acq_notes || "No description for this acquisition."}
        </DetailValue>
      </div>
    </FlyoutContainer>
  );
};

export const AcquisitionFlyout: React.FC<AcquisitionFlyoutProps> = ({
  show,
  onClose,
  defaultInfo,
  onEdit,
  onDelete,
  onNext,
  onPrevious,
  onCopy,
  caseInfo,
  evidenceInfo,
}) => {
  const queryClient = useQueryClient();
  // Restore the write-action gating the legacy StoredDataContent flyout had:
  // observers / expired licenses can read but must not see Copy/Edit/Delete.
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCopyModal, setShowCopyModal] = useState<boolean>(false);
  const [copyFromData, setCopyFromData] = useState<Acquisition | undefined>(
    undefined
  );

  const { data: itemData, isLoading } = useQuery({
    queryKey: [
      "acquisition",
      { acq_id: parseInt(defaultInfo.acq_id.toString(), 10) },
    ],
    queryFn: () =>
      AcquisitionsAPI.getAcquisitions({
        acq_id: parseInt(defaultInfo.acq_id.toString(), 10),
      }),
    initialData: [defaultInfo],
    select: (res) => {
      return res[0];
    },
  });

  const handleCopy = () => {
    setCopyFromData(itemData);
    setShowCopyModal(true);
  };

  return (
    <Flyout open={show} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {/* A refetch can resolve to no rows (deleted or nonexistent acq_id):
          select yields undefined while isLoading stays false because of
          initialData, so an unguarded render would crash on itemData. */}
      {!isLoading && !itemData && (
        <FlyoutHeader>
          <FlyoutTitle>Acquisition not found</FlyoutTitle>
          <HeaderContent>
            <div>
              This acquisition may have been deleted or the link is invalid.
            </div>
            <FlyoutNavControls onPrevious={onPrevious} onNext={onNext} />
          </HeaderContent>
        </FlyoutHeader>
      )}
      {!isLoading && itemData && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>{itemData.acq_name}</FlyoutTitle>
            <HeaderContent>
              <div>Acquisition Details</div>
              {canWrite && (
                <>
                  <Button
                    size="xxs"
                    variant="text"
                    leftSection={<CopyIcon size={14} />}
                    color="primary"
                    onClick={handleCopy}
                  >
                    Copy
                  </Button>
                  <Button
                    size="xxs"
                    variant="text"
                    leftSection={<EditIcon size={14} />}
                    color="primary"
                    onClick={() => setShowEditModal(true)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="xxs"
                    variant="text"
                    leftSection={<TrashIcon size={14} />}
                    color="error"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Delete
                  </Button>
                </>
              )}
              <FlyoutNavControls onPrevious={onPrevious} onNext={onNext} />
            </HeaderContent>
          </FlyoutHeader>
          <MainDetails defaultData={itemData} />

          <CopyAcquisitionModal
            show={showCopyModal}
            onSubmit={() => {
              queryClient.invalidateQueries({
                queryKey: ["acquisitions:list"],
              });
              onCopy?.(); // Refresh the main view
              setShowCopyModal(false);
              setCopyFromData(undefined);
            }}
            onClose={() => {
              setShowCopyModal(false);
              setCopyFromData(undefined);
            }}
            onCancel={() => {
              setShowCopyModal(false);
              setCopyFromData(undefined);
            }}
            copyFromData={copyFromData}
            caseInfo={caseInfo}
            evidenceInfo={evidenceInfo}
          />
          <EditAcquisitionModal
            show={showEditModal}
            onSubmit={() => {
              queryClient.invalidateQueries({
                queryKey: [
                  "acquisition",
                  { acq_id: parseInt(defaultInfo.acq_id.toString(), 10) },
                ],
              });
              queryClient.invalidateQueries({
                queryKey: ["acquisitions:list"],
              });
              onEdit();
              setShowEditModal(false);
            }}
            onClose={() => setShowEditModal(false)}
            onCancel={() => setShowEditModal(false)}
            defaultFormData={itemData}
            caseInfo={caseInfo}
          />

          <DeleteAcquisitionModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onDelete={() => {
              onDelete?.(); // Refetches queries in acquisition section
              setShowDeleteModal(false);
            }}
            defaultData={itemData}
          />
        </>
      )}
    </Flyout>
  );
};
